import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Theme, ThemeId } from '../themes/types';
import { getTheme } from '../themes';

interface ThemeContextType {
  theme: Theme;
  themeId: ThemeId;
  setTheme: (themeId: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeId;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, defaultTheme = 'classic' }) => {
  const [themeId, setThemeId] = useState<ThemeId>(defaultTheme);
  const [theme, setThemeState] = useState<Theme>(getTheme(defaultTheme));

  useEffect(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('quizcrunch_theme') as ThemeId;
    if (savedTheme && (savedTheme === 'classic' || savedTheme === 'retro-8bit' || savedTheme === 'halloween')) {
      setThemeId(savedTheme);
      setThemeState(getTheme(savedTheme));
    }
  }, []);

  useEffect(() => {
    const newTheme = getTheme(themeId);
    setThemeState(newTheme);

    // Apply CSS variables to document root
    const root = document.documentElement;

    // Apply colors as CSS variables
    root.style.setProperty('--color-choice1', newTheme.colors.choice1);
    root.style.setProperty('--color-choice2', newTheme.colors.choice2);
    root.style.setProperty('--color-choice3', newTheme.colors.choice3);
    root.style.setProperty('--color-choice4', newTheme.colors.choice4);
    root.style.setProperty('--color-correct', newTheme.colors.correct);
    root.style.setProperty('--color-incorrect', newTheme.colors.incorrect);
    root.style.setProperty('--color-background', newTheme.colors.background);
    root.style.setProperty('--color-card-bg', newTheme.colors.cardBackground);
    root.style.setProperty('--color-text-primary', newTheme.colors.textPrimary);
    root.style.setProperty('--color-text-secondary', newTheme.colors.textSecondary);
    root.style.setProperty('--color-accent', newTheme.colors.accent);

    // Apply font family
    root.style.setProperty('--font-family', newTheme.typography.fontFamily);

    // Inject custom CSS if provided
    let styleElement = document.getElementById('theme-custom-styles');
    if (newTheme.customCSS) {
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'theme-custom-styles';
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = newTheme.customCSS;
    } else if (styleElement) {
      styleElement.remove();
    }

    // Add theme class to body for special effects
    document.body.className = `theme-${themeId}`;

    // For retro theme, add scanlines wrapper
    if (themeId === 'retro-8bit') {
      document.body.classList.add('retro-scanlines');
    } else {
      document.body.classList.remove('retro-scanlines');
    }

    // Load Google Fonts for retro theme
    if (themeId === 'retro-8bit') {
      const linkId = 'retro-font-link';
      if (!document.getElementById(linkId)) {
        const link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap';
        document.head.appendChild(link);
      }
    }

    // Load Google Fonts for Halloween theme
    if (themeId === 'halloween') {
      const linkId = 'halloween-font-link';
      if (!document.getElementById(linkId)) {
        const link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Creepster&family=Nosifer&display=swap';
        document.head.appendChild(link);
      }
    }

    // Save to localStorage
    localStorage.setItem('quizcrunch_theme', themeId);
  }, [themeId]);

  const handleSetTheme = useCallback((newThemeId: ThemeId) => {
    setThemeId(newThemeId);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, themeId, setTheme: handleSetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};